import { useState, useEffect, useCallback, useMemo } from 'react';
import { getPoranaytteetByTarkastuskohdeV2 } from '../api/api';
import AddNameModal from './common/AddNameModal';
import DynamicPTSTaulu from './PTS/DynamicPTSTaulu';
import useKuntoarviovalikkoV2 from './report/blocks/julkisivuV2/services/useKuntoarviovalikkoV2';
import useRakenneosaNavV2 from './report/blocks/julkisivuV2/services/useRakenneosaNavV2';
import RakenneosaNavView from './report/blocks/julkisivuV2/views/RakenneosaNavView';
import ApiFeedbackBanner from './report/blocks/julkisivuV2/views/ApiFeedbackBanner';
import usePoranayteV2 from './report/blocks/julkisivuV2/services/usePoranayteV2';
import PoranayteView from './report/blocks/julkisivuV2/views/PoranayteView';
import usePeitepaksuusV2 from './report/blocks/julkisivuV2/services/usePeitepaksuusV2';
import PeitepaksuusView from './report/blocks/julkisivuV2/views/PeitepaksuusView';
import useTestiTypeV2 from './report/blocks/julkisivuV2/services/useTestiTypeV2';
import VetolujuusView from './report/blocks/julkisivuV2/views/VetolujuusView';
import OhuthieView from './report/blocks/julkisivuV2/views/OhuthieView';
import KloridipitoisuusView from './report/blocks/julkisivuV2/views/KloridipitoisuusView';
import RappausnayteView from './report/blocks/julkisivuV2/views/RappausnayteView';
import { TYYPPI as VETO_TYYPPI, DEFAULT_ARVO as VETO_DEFAULT_ARVO } from './report/blocks/julkisivuV2/VetolujuusBlockV2';
import { TYYPPI as OHUTHIE_TYYPPI, DEFAULT_ARVO as OHUTHIE_DEFAULT_ARVO } from './report/blocks/julkisivuV2/OhuthieBlockV2';
import { TYYPPI as KLORIDI_TYYPPI, DEFAULT_ARVO as KLORIDI_DEFAULT_ARVO } from './report/blocks/julkisivuV2/KloridipitoisuusBlockV2';
import { TYYPPI as RAPPAUS_TYYPPI, DEFAULT_ARVO as RAPPAUS_DEFAULT_ARVO } from './report/blocks/julkisivuV2/RappausnayteBlockV2';

// Standalone, page-level equivalent of the report-builder's Kuntoarviovalikko
// + rakenneosa-nav + measurement-block chain (julkisivuV2/), used when there
// is no enclosing report (no reportId, no ReportTemplateContext) — this page
// (KuntotutkimuksetTab) is the first place that's true. Every table's own
// hook/view pair is reused completely unchanged; only the navigation chain
// (which report-embedded blocks normally each own a private copy of) is
// hoisted up to ONE shared instance here, so switching the "table type"
// subtab (new — each table is its own report block, never tabbed together)
// keeps the same tarkastuskohde/rakenneosa selected underneath it.
const CATEGORY_ICONS = {
  Julkisivut: 'bi-building', Ikkunat: 'bi-columns-gap', Ovet: 'bi-door-open',
  Parvekkeet: 'bi-aspect-ratio', Vesikatot: 'bi-house-up', Ryömintätilat: 'bi-moisture',
};
// The only categories with real v2 measurement tables (Poränäyte,
// Peitepaksuus, etc. are julkisivu-specific) — see enabledCategories below.
const V2_SUPPORTED_CATEGORIES = ['Julkisivut', 'Parvekkeet'];

const TABLE_TYPES = [
  { key: 'poranayte', label: 'Poränäyte', scope: 'rak', canCreateRak: true },
  { key: 'peitepaksuus', label: 'Peitepaksuus', scope: 'rak', canCreateRak: false },
  { key: 'vetolujuus', label: 'Vetolujuus', scope: 'tk', tyyppi: VETO_TYYPPI, defaultArvo: VETO_DEFAULT_ARVO, View: VetolujuusView },
  { key: 'ohuthie', label: 'Ohuthie', scope: 'tk', tyyppi: OHUTHIE_TYYPPI, defaultArvo: OHUTHIE_DEFAULT_ARVO, View: OhuthieView },
  { key: 'kloridipitoisuus', label: 'Kloridipitoisuus', scope: 'tk', tyyppi: KLORIDI_TYYPPI, defaultArvo: KLORIDI_DEFAULT_ARVO, View: KloridipitoisuusView },
  { key: 'rappausnayte', label: 'Rappausnäyte', scope: 'tk', tyyppi: RAPPAUS_TYYPPI, defaultArvo: RAPPAUS_DEFAULT_ARVO, View: RappausnayteView },
];

// hasData=false mutes a type with nothing behind it yet (lighter grey,
// never bold even when active) instead of hiding it — hiding would also
// hide the only way to discover that this test type can be used here at
// all; muting just stops an empty tab competing for attention with ones
// that actually have content.
const tableTypeTabStyle = (active, hasData = true) => ({
  padding: '8px 6px', textAlign: 'center', fontSize: '0.84rem', fontWeight: active && hasData ? 800 : 600,
  color: !hasData ? '#ced4da' : active ? '#04aa00' : '#6c757d', borderBottom: '3px solid ' + (active && hasData ? '#04aa00' : '#f1f3f5'),
  background: 'none', border: 'none', borderBottomWidth: 3, borderBottomStyle: 'solid', cursor: 'pointer',
});
// Mobile-only — a flat bordered grid cell (hairline dividers between cells,
// light accent tint + text color for the active one) instead of the
// colored underline-bar style above, matching the Apple-style grouped-list
// treatment used elsewhere on mobile rather than the desktop tab look.
const mobileTableTypeCellStyle = (active, isLastCol, isLastRow, hasData = true) => ({
  padding: '12px 4px', textAlign: 'center', fontSize: '0.78rem', fontWeight: active && hasData ? 700 : 400,
  color: !hasData ? '#c7c7cc' : active ? '#198754' : '#1c1c1e', background: active && hasData ? '#f7fdf9' : 'none',
  border: 'none', borderRight: isLastCol ? 'none' : '0.5px solid #e5e5ea', borderBottom: isLastRow ? 'none' : '0.5px solid #e5e5ea',
  cursor: 'pointer',
});

// Same 768px breakpoint/pattern as PeitepaksuusView.jsx etc. — below it, the
// rail (fixed 260px) sitting beside the content in a row forces the whole
// page to scroll horizontally on a phone, which also throws off any
// position:fixed modal opened from further down the page (it centers on
// the true viewport, not wherever the page happened to be scrolled to).
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

function PoranayteTablePanel({ rakId, onTestiPresenceChange }) {
  const svc = usePoranayteV2(rakId);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Creates immediately — addSample auto-names it PN1/PN2/... when no
  // tunnus is given, and that's just an editable field in the table/cards
  // afterward, so there's nothing a name-first modal added here.
  const addNayte = () => svc.addSample().finally(onTestiPresenceChange);
  const pendingDeleteSample = svc.samples.find(s => s.id === pendingDeleteId);
  // toggleTesti is the only thing here that can change whether a
  // vetolujuus/ohuthie/kloridipitoisuus/rappausnäyte tab has any eligible
  // rows (see useTestiTypeV2) — refresh the mute/unmute signal above once
  // it settles, so ticking a box there un-mutes the matching tab live
  // instead of waiting for the next tkId-triggered refetch.
  const toggleTestiAndNotify = (sample, opt, checked) => svc.toggleTesti(sample, opt, checked).finally(onTestiPresenceChange);

  return (
    <>
      <ApiFeedbackBanner feedback={svc.feedback} onClose={svc.clearFeedback} />
      <PoranayteView {...svc} toggleTesti={toggleTestiAndNotify} removeSample={id => setPendingDeleteId(id)} taulukkoNum={1} onAddSample={addNayte} />

      {pendingDeleteSample && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1060, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setPendingDeleteId(null); }}>
          <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.22)', width: 'min(360px, 96vw)', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Poista näyte</div>
            <div style={{ fontSize: '0.92rem', color: '#495057' }}>
              Haluatko poistaa näytteen <strong>{pendingDeleteSample.nayte_tunnus || `#${pendingDeleteSample.id}`}</strong>?
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setPendingDeleteId(null)}>Peru</button>
              <button className="btn btn-sm btn-danger" onClick={() => { const id = pendingDeleteId; setPendingDeleteId(null); svc.removeSample(id).finally(onTestiPresenceChange); }}>Poista</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PeitepaksuusTablePanel({ rakId }) {
  const svc = usePeitepaksuusV2(rakId);
  return (
    <>
      <ApiFeedbackBanner feedback={svc.feedback} onClose={svc.clearFeedback} />
      <PeitepaksuusView {...svc} taulukkoNum={1} />
    </>
  );
}

function TestiTypeTablePanel({ tkId, tyyppi, defaultArvo, View }) {
  const svc = useTestiTypeV2(tkId, tyyppi, defaultArvo);
  return (
    <>
      <ApiFeedbackBanner feedback={svc.feedback} onClose={svc.clearFeedback} />
      <View {...svc} taulukkoNum={1} />
    </>
  );
}

export default function KuntoarvioWorkspaceV2({ id_kiinteisto, ptsProjectId, onPtsProjectChange }) {
  const kuntoarvioValue = useMemo(() => ({ ptsProjectId }), [ptsProjectId]);
  const noopChange = useCallback(() => {}, []);
  // standalone=true: see useKuntoarviovalikkoV2 — there is no report here to
  // attach a cloned section to, which is the expected (not erroneous) case.
  const kuntoarvioSvc = useKuntoarviovalikkoV2(id_kiinteisto, kuntoarvioValue, noopChange, undefined, undefined, undefined, true);

  const [rakNavData, setRakNavData] = useState({});
  // linkedProjectId=ptsProjectId keeps this chain on the same PTS project as
  // the rail above, same "linkedProjectId always wins" contract a report's
  // own blocks already use to follow Kuntoarviovalikko's choice.
  const rakNavSvc = useRakenneosaNavV2(id_kiinteisto, rakNavData, setRakNavData, ptsProjectId, null);

  // Only categories with real v2 julkisivu tables behind them — currently
  // Julkisivut and Parvekkeet. getKategoriat() (kuntoarvioSvc.kategoriat)
  // is NOT scoped to this already — it's the full kuntoarvio category
  // menu (also returns Ikkunat/Ovet/Vesikatot/Ryömintätilat), so this list
  // is filtered explicitly here rather than trusted as-is. Those other
  // categories stay out of this menu entirely rather than showing as
  // disabled entries — they have nothing here to link to yet.
  const enabledCategories = useMemo(
    () => kuntoarvioSvc.kategoriat.map(c => c.value).filter(v => V2_SUPPORTED_CATEGORIES.includes(v)),
    [kuntoarvioSvc.kategoriat],
  );

  // Which category the add-tarkastuskohde modal targets — set by whichever
  // category's own "+" the user clicked, not a persistent "selected"
  // category (there's no longer a concept of one: the tarkastuskohde tabs
  // below always show every tarkastuskohde for this PTS project, matching
  // how RakenneosaNavView already behaves inside a report).
  const [addTkCategory, setAddTkCategory] = useState(null);
  const [showAddTk, setShowAddTk] = useState(false);
  const [newTkName, setNewTkName] = useState('');
  const openAddTk = (cat) => { setAddTkCategory(cat); setNewTkName(''); setShowAddTk(true); };

  // Shared by both the custom-name modal and the one-click suggestion chips
  // below — adds the tarkastuskohde, then (since creating one through
  // useKuntoarviovalikkoV2 doesn't change rakNavSvc's own ptsProjectId, so
  // its effects never refire on their own) refetches rakNavSvc's list and
  // opens the newly created one by matching its id in the fresh result.
  const addTk = useCallback(async (cat, name) => {
    const created = await kuntoarvioSvc.addTarkastuskohde(cat, name);
    if (created) {
      const fresh = await rakNavSvc.refreshTarkastuskohteet();
      const match = fresh.find(tk => tk.id === created.id);
      if (match) rakNavSvc.openTk(match);
    }
  }, [kuntoarvioSvc, rakNavSvc]);

  const submitAddTk = async () => {
    const name = newTkName.trim();
    if (!name || !addTkCategory) return;
    setShowAddTk(false);
    await addTk(addTkCategory, name);
  };

  const [tableType, setTableType] = useState('poranayte');
  const activeType = TABLE_TYPES.find(t => t.key === tableType);

  // Which table-type tabs actually have anything behind them, so empty ones
  // can be muted instead of competing visually with ones that do (Apple-
  // style: don't hide the way to create something, but don't give an empty
  // table the same visual weight as a filled one either). Poränäyte itself
  // is rak-scoped (has data = any sample on the CURRENT rakenneosa);
  // vetolujuus/ohuthie/kloridipitoisuus/rappausnäyte are tk-scoped and only
  // ever get rows for samples tagged with that testi type anywhere under
  // the tarkastuskohde (see useTestiTypeV2) — one shared fetch of the
  // tarkastuskohde's poränäyte samples (tagged with their source
  // rakenneosa) is enough to derive presence for all five without mounting
  // every type's own hook just to check. Peitepaksuus isn't driven by
  // poränäyte samples at all, so it's left out and always shown normal.
  const [tkSamplesForPresence, setTkSamplesForPresence] = useState([]);
  const { tkId: presenceTkId } = rakNavSvc.nav;
  const refreshTypePresence = useCallback(() => {
    if (!presenceTkId) { setTkSamplesForPresence([]); return; }
    getPoranaytteetByTarkastuskohdeV2(presenceTkId).then(r => setTkSamplesForPresence(r.data || [])).catch(() => {});
  }, [presenceTkId]);
  useEffect(refreshTypePresence, [refreshTypePresence]);
  const hasTypeData = useCallback((type) => {
    if (type.key === 'peitepaksuus') return true;
    if (type.key === 'poranayte') return tkSamplesForPresence.some(s => s.id_rakenneosa === rakNavSvc.nav.rakId);
    return tkSamplesForPresence.some(s => (s.testi || '').toLowerCase().includes(type.tyyppi.toLowerCase()));
  }, [tkSamplesForPresence, rakNavSvc.nav.rakId]);

  // PTS is a view-mode toggle, not a tarkastuskohde — selecting it swaps
  // the table-type tabs + table panel below for DynamicPTSTaulu, without
  // touching rakNavSvc.nav.tkId (the rakenneosa tray stays showing whatever
  // tarkastuskohde was last open). Picking a tarkastuskohde or table-type
  // tab turns it back off, same as switching between any two exclusive
  // views would.
  const [showPts, setShowPts] = useState(false);
  const pickTableType = (key) => { setShowPts(false); setTableType(key); };
  const rakNavSvcForView = useMemo(() => ({
    ...rakNavSvc,
    openTk: (tk) => { setShowPts(false); rakNavSvc.openTk(tk); },
  }), [rakNavSvc]);

  const isMobile = useIsMobile();

  // Auto-open a tarkastuskohde on load so the user doesn't have to click
  // one every time they land here — pick the first one once the list
  // loads, same "only while nothing's chosen yet" guard the rakenneosa
  // auto-select below it already uses, so it never overrides a tab the
  // user picked by hand afterwards.
  const { tkId: autoOpenTkId } = rakNavSvc.nav;
  const { tarkastuskohteet, openTk } = rakNavSvc;
  useEffect(() => {
    if (autoOpenTkId || !tarkastuskohteet.length) return;
    openTk(tarkastuskohteet[0]);
  }, [autoOpenTkId, tarkastuskohteet, openTk]);

  if (!id_kiinteisto) return null;

  return (
    <div className="border rounded overflow-hidden">
      <div className="px-3 py-2 fw-semibold" style={{ background: '#f8f9fa', fontSize: '0.9rem' }}>
        Kuntoarvio (V2)
      </div>
      <div className="p-2 border-top bg-white" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 18 }}>

        {/* Level 1: Kuntoarviovalikko — the full category menu, each with
            its own already-chosen tarkastuskohteet listed inline below it,
            same shape as the report's own KuntoarviovalikkoView (a category
            box with its added subsections shown as checkmark chips) rather
            than a plain nav list. Stacks above the content below 768px
            instead of sitting beside it — a fixed 260px rail next to the
            content forced the whole page to scroll horizontally on a
            phone, which also threw off position:fixed modals opened from
            further down the page (they center on the true viewport, not
            wherever the page had scrolled to). */}
        <div style={{ width: isMobile ? '100%' : 260, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {!isMobile && (
            <div style={{ fontSize: '0.64rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#6c757d', padding: '4px 8px 10px' }}>
              Kuntoarviovalikko
            </div>
          )}
          {enabledCategories.map((cat, i) => {
            const chosen = kuntoarvioSvc.tkByCategory[cat] || [];
            const chosenNames = new Set(chosen.map(tk => tk.nimi));
            // Ready-to-use suggestions (static list + real historical names
            // from this property's own image subcategories — see
            // useKuntoarviovalikkoV2's suggestionsByCategory), same as the
            // report's own KuntoarviovalikkoView: one click adds it, no
            // typing needed. Already-chosen ones are filtered out here since
            // they're already shown as checkmark chips above.
            const suggestions = (kuntoarvioSvc.suggestionsByCategory[cat] || []).filter(s => !chosenNames.has(s));
            return isMobile ? (
              // Apple-style flat grouped list — one accent color (checkmark
              // + text, not a filled chip), hairline dividers instead of
              // colored pill borders, matching RakenneosaNavView's mobile
              // sections for visual consistency down the whole page.
              <div key={cat} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8e8e93', margin: '0 2px 7px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className={`bi ${CATEGORY_ICONS[cat] ?? 'bi-folder'}`} style={{ fontSize: '0.78rem' }} />
                  {cat}
                </div>
                {/* Three visually distinct roles, not two colors doing
                    double duty: "chosen" and "suggestion" rows share the
                    SAME normal dark text color (standard list-row look,
                    telling them apart by their trailing accessory — ✓ vs
                    +) — only the always-present "add custom name" action
                    row at the bottom is colored, since it's the one true
                    action here, not a list entry. */}
                <div style={{ background: '#fff', border: '1px solid #e5e5ea', borderRadius: 14, overflow: 'hidden' }}>
                  {chosen.map((tk, idx) => (
                    <div key={tk.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: (idx < chosen.length - 1 || suggestions.length > 0) ? '0.5px solid #e5e5ea' : 'none', fontSize: '0.9rem', color: '#1c1c1e' }}>
                      <span style={{ flex: 1 }}>{tk.nimi}</span>
                      <span style={{ color: '#198754', fontWeight: 700 }}>✓</span>
                    </div>
                  ))}
                  {suggestions.map((s, idx) => {
                    const isSaving = kuntoarvioSvc.savingKey === cat + ':' + s;
                    return (
                      <button key={s} type="button" disabled={isSaving} onClick={() => addTk(cat, s)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                          padding: '12px 14px', border: 'none', background: 'none',
                          borderBottom: idx < suggestions.length - 1 ? '0.5px solid #e5e5ea' : 'none',
                          fontSize: '0.9rem', color: '#1c1c1e', cursor: isSaving ? 'wait' : 'pointer', opacity: isSaving ? 0.6 : 1,
                        }}>
                        <span style={{ flex: 1 }}>{isSaving ? 'Lisätään…' : s}</span>
                        {!isSaving && <span style={{ color: '#198754', fontWeight: 700 }}>+</span>}
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => openAddTk(cat)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', background: 'none', borderTop: (chosen.length + suggestions.length) > 0 ? '0.5px solid #e5e5ea' : 'none', color: '#198754', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
                    + Uusi osio
                  </button>
                </div>
              </div>
            ) : (
              <div key={cat} style={{ padding: '0 4px 14px', marginBottom: 14, borderBottom: i < enabledCategories.length - 1 ? '1px solid #f1f3f5' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <i className={`bi ${CATEGORY_ICONS[cat] ?? 'bi-folder'}`} style={{ fontSize: '0.85rem', color: '#198754' }} />
                  <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 700, color: '#343a40' }}>{cat}</div>
                  <button type="button" onClick={() => openAddTk(cat)} title="Lisää tarkastuskohde"
                    style={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', border: '1px dashed #198754', background: '#fff', color: '#198754', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    +
                  </button>
                </div>

                {chosen.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {chosen.map(tk => (
                      <span key={tk.id} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.74rem', fontWeight: 600, color: '#15803d', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 20, padding: '3px 9px' }}>
                        <i className="bi bi-check" style={{ fontSize: '0.7rem' }} />{tk.nimi}
                      </span>
                    ))}
                  </div>
                )}

                {suggestions.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: chosen.length > 0 ? 6 : 0 }}>
                    {suggestions.map(s => {
                      const isSaving = kuntoarvioSvc.savingKey === cat + ':' + s;
                      return (
                        <button key={s} type="button" disabled={isSaving} onClick={() => addTk(cat, s)}
                          style={{ border: '1px solid #e9ecef', background: '#f8f9fa', color: '#868e96', borderRadius: 20, padding: '3px 10px', fontSize: '0.74rem', fontWeight: 500, cursor: isSaving ? 'wait' : 'pointer', opacity: isSaving ? 0.6 : 1 }}>
                          {isSaving ? '…' : s}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Levels 2-3: tarkastuskohde tabs + rakenneosa tray (reused as-is,
            always showing every tarkastuskohde for this PTS project —
            unfiltered by the rail above, same as inside a report). */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Normally the tray only shows for scope='rak' table types
              (Poränäyte/Peitepaksuus) — scope='tk' types don't need a
              rakenneosa picker. But that left no way to create a FIRST
              rakenneosa at all when switching to a tarkastuskohde that has
              none while a scope='tk' type (e.g. Vetolujuus) was still the
              active tab from a previous tarkastuskohde — the tray (and its
              only "+ Rakenneosa" button) stayed hidden until a refresh
              reset the tab back to Poränäyte. Force it open whenever the
              current tarkastuskohde has zero rakenneosa, regardless of
              which table type is active. */}
          <RakenneosaNavView id_kiinteisto={id_kiinteisto} svc={rakNavSvcForView}
            canCreateRakenneosa={activeType.canCreateRak || rakNavSvc.rakenneosat.length === 0}
            showRakenneosaTabs={activeType.scope === 'rak' || rakNavSvc.rakenneosat.length === 0}
            trailingTab={{ label: 'PTS', active: showPts, onClick: () => setShowPts(v => !v) }} />

          {showPts ? (
            <div style={{ overflowX: 'auto' }}>
              <DynamicPTSTaulu data={{ projectId: ptsProjectId }} onChange={onPtsProjectChange} />
            </div>
          ) : rakNavSvc.nav.tkId && rakNavSvc.rakenneosat.length === 0 ? (
            // No rakenneosa under this tarkastuskohde yet — every table
            // type is driven by rakenneosa data one way or another
            // (Poränäyte/Peitepaksuus directly by rakId, the testi-type
            // tables indirectly via Poränäyte's own samples), so there is
            // nothing any of them could show yet. Showing the full 6-type
            // grid anyway (as dead/disabled-looking tabs leading nowhere)
            // was confusing — the rakenneosa tray above already has the
            // "+ Rakenneosa" action to fix that.
            <div style={{ padding: '18px 4px', textAlign: 'center', color: '#8e8e93', fontSize: '0.84rem' }}>
              Lisää ensin rakenneosa yllä nähdäksesi taulukot.
            </div>
          ) : (
            /* Level 4: table type — new, since each of these is its own
               separate report block and never tabbed together there. */
            rakNavSvc.nav.tkId && (
              <>
                {/* Grid (not flex+marginRight) so every tab is the same
                    width regardless of label length, instead of ragged
                    widths wrapping unevenly. 3 columns reads as a clean
                    2-row grid for the current 6 table types. On mobile this
                    becomes a bordered card with hairline cell dividers
                    (Apple grouped-list look) instead of the colored
                    underline-bar style used on desktop. */}
                {isMobile ? (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8e8e93', margin: '0 2px 7px' }}>Taulukko</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: '#fff', border: '1px solid #e5e5ea', borderRadius: 14, overflow: 'hidden' }}>
                      {TABLE_TYPES.map((t, i) => (
                        <button key={t.key} type="button"
                          style={mobileTableTypeCellStyle(t.key === tableType, (i + 1) % 3 === 0, i >= TABLE_TYPES.length - (TABLE_TYPES.length % 3 || 3), hasTypeData(t))}
                          onClick={() => pickTableType(t.key)}>
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 8px', marginBottom: 12 }}>
                    {TABLE_TYPES.map(t => (
                      <button key={t.key} type="button" style={tableTypeTabStyle(t.key === tableType, hasTypeData(t))} onClick={() => pickTableType(t.key)}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}

                {activeType.scope === 'rak' && rakNavSvc.nav.rakId && (
                  activeType.key === 'poranayte'
                    ? <PoranayteTablePanel rakId={rakNavSvc.nav.rakId} onTestiPresenceChange={refreshTypePresence} />
                    : <PeitepaksuusTablePanel rakId={rakNavSvc.nav.rakId} />
                )}
                {activeType.scope === 'tk' && (
                  <TestiTypeTablePanel key={activeType.key} tkId={rakNavSvc.nav.tkId}
                    tyyppi={activeType.tyyppi} defaultArvo={activeType.defaultArvo} View={activeType.View} />
                )}
              </>
            )
          )}
        </div>
      </div>

      {showAddTk && (
        <AddNameModal title={`Lisää tarkastuskohde (${addTkCategory})`} placeholder="esim. Asuntojen parvekkeet"
          value={newTkName} onChange={setNewTkName} onSubmit={submitAddTk} onClose={() => setShowAddTk(false)} />
      )}
    </div>
  );
}
